import React from "react";
import { AddCommaNumber } from "../../helpers/Utils";

const UpsellInline = ({ order, onUpgradeSize, onAddTopping }) => {
  const sizes = order.sizeSettings || [];
  const toppings = order.toppingSettings || [];

  if (!sizes.length && !toppings.length) return null;

  // Find current size index to show upgrade options
  // Support both F&B (sizeSelectedId) and Fashion/Retail (selectedAttributes.size)
  let currentSizeId = order.sizeSelectedId?.[0];
  let isFashionSize = false;

  // Fallback for fashion/retail: match selectedAttributes.size to sizeSettings by name
  if (!currentSizeId && order.selectedAttributes?.size) {
    isFashionSize = true;
    const selectedSizeLabel =
      order.selectedAttributes.size.label ||
      order.selectedAttributes.size.value;
    const matchedSize = sizes.find(
      (s) => s.name === selectedSizeLabel || s.name === order.size,
    );
    if (matchedSize) {
      currentSizeId = matchedSize.id;
    }
  }

  // Fallback: match order.size string label directly
  if (!currentSizeId && order.size && typeof order.size === "string") {
    isFashionSize = true;
    const matchedByLabel = sizes.find((s) => s.name === order.size);
    if (matchedByLabel) {
      currentSizeId = matchedByLabel.id;
    }
  }

  const sortedSizes = [...sizes].sort(
    (a, b) => parseFloat(a.value) - parseFloat(b.value),
  );

  let upgradeSizes;
  if (isFashionSize && currentSizeId) {
    // Fashion/Retail: show ALL sizes except the currently selected one
    upgradeSizes = sortedSizes.filter((s) => s.id != currentSizeId);
  } else if (currentSizeId) {
    // F&B: only show sizes LARGER than current (higher price = upgrade)
    const currentSortedIdx = sortedSizes.findIndex(
      (s) => s.id == currentSizeId,
    );
    upgradeSizes = sortedSizes.filter((_, idx) => idx > currentSortedIdx);
  } else {
    upgradeSizes = sortedSizes;
  }

  // Filter out already selected toppings
  const selectedToppingIds = order.toppingSelectedId || [];
  const availableToppings = toppings.filter(
    (t) => !selectedToppingIds.includes(t.id),
  );

  if (!upgradeSizes.length && !availableToppings.length) return null;

  // Current size effective price for calculating diff
  const currentSize = currentSizeId
    ? sortedSizes.find((s) => s.id == currentSizeId)
    : null;
  const currentSizePrice = currentSize
    ? parseFloat(currentSize.promo_price) > 0
      ? parseFloat(currentSize.promo_price)
      : parseFloat(currentSize.value) || 0
    : parseFloat(order.price) || 0;

  return (
    <div
      className="d-flex flex-wrap align-items-center px-2 pb-1"
      style={{ gap: "4px" }}
    >
      {upgradeSizes.map((size) => {
        let sizePrice;

        if (isFashionSize && order.variants?.length) {
          // Fashion: find variant for this size + current other attributes
          const testAttrs = { ...order.selectedAttributes };
          testAttrs.size = { value: size.name, label: size.name };
          const variant = order.variants.find((v) =>
            Object.keys(testAttrs).every(
              (k) => v.attributes?.[k]?.value === testAttrs[k]?.value,
            ),
          );
          if (variant) {
            const promo = parseFloat(variant.promo_price);
            const base = parseFloat(variant.price);
            sizePrice =
              !isNaN(promo) && promo > 0 ? promo : !isNaN(base) ? base : 0;
          } else {
            sizePrice =
              parseFloat(size.promo_price) > 0
                ? parseFloat(size.promo_price)
                : parseFloat(size.value) || 0;
          }
        } else {
          sizePrice =
            parseFloat(size.promo_price) > 0
              ? parseFloat(size.promo_price)
              : parseFloat(size.value) || 0;
        }

        const diff = sizePrice - currentSizePrice;
        return (
          <span
            key={size.id}
            className="badge badge-outline-primary cursor-pointer"
            style={{
              fontSize: "10px",
              padding: "2px 6px",
              borderRadius: "10px",
              border: "1px dashed #145388",
            }}
            onClick={() => onUpgradeSize && onUpgradeSize(order, size)}
          >
            {size.name}{" "}
            {isFashionSize
              ? sizePrice > 0
                ? `- ${AddCommaNumber(sizePrice)}`
                : ""
              : diff > 0
                ? `+${AddCommaNumber(diff)}`
                : ""}
          </span>
        );
      })}
      {availableToppings.slice(0, 3).map((topping) => {
        const price =
          parseFloat(topping.promo_price) > 0
            ? parseFloat(topping.promo_price)
            : parseFloat(topping.value) || 0;
        return (
          <span
            key={topping.id}
            className="badge badge-outline-success cursor-pointer"
            style={{
              fontSize: "10px",
              padding: "2px 6px",
              borderRadius: "10px",
              border: "1px dashed #2e7d32",
            }}
            onClick={() => onAddTopping && onAddTopping(order, topping)}
          >
            +{topping.name} {price > 0 ? AddCommaNumber(price) : ""}
          </span>
        );
      })}
    </div>
  );
};

export default UpsellInline;
